// useBatchSchedulerRows — per-row scheduler flags for the BulkOrders detail.
//
// Wraps `get_batch_scheduler_rows`. Used to highlight individual rows as
// `tight_window`, `urgent`, `unscheduled`, `same_day_turnover`. No client
// fallback: when the RPC fails the panel keeps its current row rendering and
// only loses the per-row badges (graceful degradation).

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchBatchSchedulerRows,
  type SchedulerError,
  type SchedulerSource,
  type ServerBatchRowFlags,
} from "@/lib/scheduler";
import type { UseSchedulerState } from "./types";

export interface UseBatchSchedulerRowsOptions {
  companyId: string | null | undefined;
  batchId: string | null | undefined;
  enabled?: boolean;
}

export function useBatchSchedulerRows(
  options: UseBatchSchedulerRowsOptions,
): UseSchedulerState<ServerBatchRowFlags[]> {
  const { companyId, batchId, enabled = true } = options;
  const [data, setData] = useState<ServerBatchRowFlags[] | null>(null);
  const [source, setSource] = useState<SchedulerSource | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<SchedulerError | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !companyId || !batchId) {
      setData(null);
      setSource(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      const result = await fetchBatchSchedulerRows({ companyId, batchId });
      if (cancelled) return;
      if (result.data) {
        setData(result.data);
        setSource(result.source);
        setError(null);
      } else {
        setData(null);
        setSource(null);
        setError(result.error);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, batchId, enabled, tick]);

  return useMemo(
    () => ({ data, source, isLoading, error, refetch }),
    [data, source, isLoading, error, refetch],
  );
}
